import { useEffect, useState, type FormEvent } from "react";
import {
  CLIENT_TYPE_LABELS,
  LEAD_SOURCE_LABELS,
  type Client,
  type ClientType,
  type LeadSource,
} from "@shared/modules/x-atelier";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { Modal } from "../../../core/components/ui/modal";
import { useToastStore } from "../../../core/stores/toast";
import { createClient, updateClient, type ClientInput } from "../lib/api";

const FORM_ID = "atelier-client-form";

interface FormState {
  type: ClientType;
  name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  smsConsent: boolean;
  source: LeadSource;
  notes: string;
}

function toForm(c?: Client): FormState {
  return {
    type: c?.type ?? "particulier",
    name: c?.name ?? "",
    phone: c?.phone ?? "",
    email: c?.email ?? "",
    address: c?.address ?? "",
    city: c?.city ?? "",
    postalCode: c?.postalCode ?? "",
    smsConsent: c?.smsConsent ?? false,
    source: c?.source ?? "appel_direct",
    notes: c?.notes ?? "",
  };
}

const orNull = (s: string) => (s.trim() ? s.trim() : null);

interface ClientFormModalProps {
  open: boolean;
  client?: Client;
  onClose: () => void;
  onSaved: (client: Client) => void;
}

/** Création ou édition complète d'un client (coordonnées, consentement SMS, source, notes). */
export function ClientFormModal({ open, client, onClose, onSaved }: ClientFormModalProps) {
  const push = useToastStore((s) => s.push);
  const [form, setForm] = useState<FormState>(() => toForm(client));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(toForm(client));
  }, [open, client]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || saving) return;
    const payload: ClientInput = {
      type: form.type,
      name: form.name.trim(),
      phone: orNull(form.phone),
      email: form.email.trim() || null,
      address: orNull(form.address),
      city: orNull(form.city),
      postalCode: orNull(form.postalCode),
      smsConsent: form.smsConsent,
      source: form.source,
      notes: orNull(form.notes),
    };
    setSaving(true);
    try {
      const saved = client ? await updateClient(client.id, payload) : await createClient(payload);
      push("success", client ? "Client mis à jour" : "Client créé", saved.name);
      onSaved(saved);
    } catch {
      push(
        "danger",
        client ? "Échec de la mise à jour" : "Échec de la création",
        "Vérifiez les champs saisis.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={client ? "Modifier le client" : "Nouveau client"}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="primary"
            disabled={saving || !form.name.trim()}
          >
            {saving ? "Enregistrement…" : client ? "Enregistrer" : "Créer le client"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="atelier-form" onSubmit={submit}>
        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Identité</h3>
          </div>
          <div className="form-section__grid">
            <Field label="Nom">
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="ex : Marie Tremblay"
                autoFocus
                required
              />
            </Field>
            <Field label="Type">
              <Select value={form.type} onChange={(e) => set("type", e.target.value as ClientType)}>
                {Object.entries(CLIENT_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Téléphone">
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="ex : 418 555-0134"
              />
            </Field>
            <Field label="Courriel">
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field label="Source">
              <Select
                value={form.source}
                onChange={(e) => set("source", e.target.value as LeadSource)}
              >
                {Object.entries(LEAD_SOURCE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <label className="atelier-check">
            <input
              type="checkbox"
              checked={form.smsConsent}
              onChange={(e) => set("smsConsent", e.target.checked)}
            />
            Consent aux SMS promotionnels
          </label>
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Adresse</h3>
          </div>
          <div className="form-section__grid form-section__grid--full">
            <Field label="Rue">
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} />
            </Field>
          </div>
          <div className="form-section__grid">
            <Field label="Ville">
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
            </Field>
            <Field label="Code postal">
              <Input
                value={form.postalCode}
                onChange={(e) => set("postalCode", e.target.value)}
                placeholder="ex : G1R 2L3"
              />
            </Field>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Notes</h3>
          </div>
          <Textarea
            rows={3}
            aria-label="Notes"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            placeholder="Préférences, historique, points d'attention…"
          />
        </section>
      </form>
    </Modal>
  );
}
